import React, { useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import { ExpandableGrid } from 'src/components/expandable-grid';
import TextLink from '@prisjakt/ui-text-link';
import StoreLink from '../store-link';
import { AuthorizedByBrand, Award } from '../../../../types';
import { SHOP_LOGO_WIDTH } from '../../constants';

import * as S from './styles';
import {
  getPaymentOptions,
  getPaymentProviders,
} from 'src/components/store-payment/utils';
import {
  IAboutProps,
  DeliveryOptionsTranslationEnum,
  DeliveryOptionsTranslationKey,
} from './types';
import PaymentProviders from './payment/payment-providers';
import PaymentOptions from './payment/payment-options';

const cardLimit = 4;

const About = ({ className, store, storeLinkClick }: IAboutProps) => {
  const { linkTo, t, market } = useContext(AppContext);

  const highResolutionLogo = store.logo
    ? { url: store.logo, width: SHOP_LOGO_WIDTH }
    : null;

  const getAwardItem = (award: Award | null) => {
    if (!award || !award.name) return null;

    return (
      <S.Card
        key={award.name}
        imgUrl={award.logo}
        title={award.name}
        limit={cardLimit}
        year={award.year?.toString()}
        link={linkTo('shopOfTheYearWinners', { year: award.year || '' })}
        type="soty"
      />
    );
  };

  const getAuthorizedDealerItem = (authorizedDealer: AuthorizedByBrand) => {
    if (!authorizedDealer.name) return null;

    return (
      <S.Card
        imgUrl={authorizedDealer.logo}
        title={authorizedDealer.name}
        key={authorizedDealer.name}
        limit={cardLimit}
        link={linkTo('brand', authorizedDealer)}
      />
    );
  };
  const paymentProviders = getPaymentProviders(store, market);
  const paymentOptions = getPaymentOptions(store, t);

  return (
    <div className={className}>
      {store.featured ? (
        <S.StoreLogo
          name={store.name}
          storeId={store.id}
          featured={store.featured}
          hasImage={Boolean(store.hasLogo)}
          logoUrl={highResolutionLogo?.url}
          width={highResolutionLogo?.width}
          showHighResolutionLogo={Boolean(highResolutionLogo)}
        />
      ) : null}
      <S.GridWrapper>
        <S.LocationAndCurrencyInfo
          countryCode={store.countryCode}
          countryName={store.countryName}
          currency={store.currency}
          id={store.id.toString()}
          marketplace={Boolean(store.marketplace)}
          storeName={store.name}
        />
      </S.GridWrapper>
      <S.StoreInfo>
        {Boolean(
          paymentOptions.hasOptions || paymentProviders.hasProviders
        ) && (
          <div>
            <S.Title>{t('store', 'store.payment-options')}</S.Title>
            {paymentProviders.hasProviders ? (
              <PaymentProviders providers={paymentProviders.providers} />
            ) : null}
            {paymentOptions.hasOptions ? (
              <PaymentOptions options={paymentOptions.options} />
            ) : null}
          </div>
        )}
        {store.deliveryOptions?.length ? (
          <div>
            <S.Title>{t('store', 'store.delivery-options')}</S.Title>
            <S.DeliveryOptionList>
              {store.deliveryOptions.map(
                option =>
                  option && (
                    <S.DeliveryOption key={option}>
                      <S.CheckIcon size={22} />
                      {t(
                        'store',
                        `store.${DeliveryOptionsTranslationEnum[option]}` as DeliveryOptionsTranslationKey
                      )}
                    </S.DeliveryOption>
                  )
              )}
            </S.DeliveryOptionList>
          </div>
        ) : null}

        {Boolean(
          store.supportTelephone ||
            store.supportOnsite ||
            store.languages?.support?.length
        ) && (
          <div>
            <S.Title>{t('store', 'store.contact')}</S.Title>
            {store.languages?.support?.length && (
              <p>
                {t('store', 'store.languages', {
                  languages: store.languages.support.join(', '),
                })}
              </p>
            )}
            {store.supportTelephone && (
              <p>{t('store', 'store.telephone-support')}</p>
            )}
            {store.supportOnsite && (
              <p>{t('store', 'store.installation-support')}</p>
            )}
            {store.featured && store.externalUri && (
              <StoreLink store={store} storeLinkClick={storeLinkClick} />
            )}
          </div>
        )}

        {Boolean(
          store.companyName || store.companyNumber || store.companyInfoUrl
        ) && (
          <div>
            <S.Title>{t('store', 'store.company-info')}</S.Title>
            {store.companyName && (
              <p>
                {t('store', 'store.company-name')} {store.companyName}
              </p>
            )}
            {store.companyNumber && (
              <p>
                {t('store', 'store.company-number')} {store.companyNumber}
              </p>
            )}
            {store.featured && store.companyInfoUrl && (
              <p>
                <span>{t('store', 'store.company-information-link')}</span>{' '}
                <TextLink to={store.companyInfoUrl} isExternal>
                  {t('store', 'store.company-information-url')}
                </TextLink>
              </p>
            )}
          </div>
        )}
      </S.StoreInfo>

      {store.storeOfTheYearAwards?.length ? (
        <S.Awards>
          <S.Title>{t('store', 'shop-of-the-year.awards')}</S.Title>
          <ExpandableGrid
            limit={cardLimit}
            expandText={t('common', 'button.show-more')}
            collapseText=""
            items={store.storeOfTheYearAwards?.map(getAwardItem)}
            canCollapse={false}
          />
        </S.Awards>
      ) : null}

      {store.authorizedBy?.length ? (
        <S.AuthorizedDealers>
          <S.Title>{t('store', 'brand.recommended-retailer')}</S.Title>
          <ExpandableGrid
            limit={cardLimit}
            expandText={t('common', 'button.show-more')}
            collapseText=""
            items={store.authorizedBy?.map(getAuthorizedDealerItem)}
            canCollapse={false}
          />
        </S.AuthorizedDealers>
      ) : null}
    </div>
  );
};

export default About;
